import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';

// See ui/docs/simkura-design.md — Components / Button.
// Shape rule: destructive and primary actions are pill-radius; secondary
// and tertiary are rect (sm radius). Variant defaults follow this rule
// unless `shape` is explicitly overridden.
//
// Default variant is `secondary` (white rounded rectangle): the app's house
// style renders every action button this way. A blue emphasis button is still
// available on demand via an explicit `variant="primary"`.
type ButtonSize = 'sm' | 'md';
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success';
type ButtonShape = 'pill' | 'rect';

interface ButtonProps {
  size?: ButtonSize;
  variant?: ButtonVariant;
  shape?: ButtonShape;
}

const sizeMap = {
  sm: { height: '28px', padding: '0 12px', fontSize: 'xs' as const },
  md: { height: '36px', padding: '0 20px', fontSize: 'md' as const },
};

const defaultShape = (variant: ButtonVariant): ButtonShape =>
  (variant === 'primary' || variant === 'danger' || variant === 'success') ? 'pill' : 'rect';

export const Button = styled('button', {
  shouldForwardProp: prop => isPropValid(prop) && !['variant', 'size', 'shape'].includes(prop)
})<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  height: ${({ size = 'md' }) => sizeMap[size].height};
  padding: ${({ size = 'md' }) => sizeMap[size].padding};
  font-size: ${({ size = 'md', theme }) => theme.font.size[sizeMap[size].fontSize]};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  font-family: ${({ theme }) => theme.font.family};
  border-radius: ${({ shape, variant = 'secondary', theme }) =>
    (shape ?? defaultShape(variant)) === 'pill'
      ? theme.border.radius.pill
      : theme.border.radius.sm};
  border: none;
  cursor: pointer;
  transition: ${({ theme }) => theme.animation.transition.clickable};
  white-space: nowrap;
  line-height: 1;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusShadow(theme.accent.tertiary)};
  }

  ${({ variant = 'secondary', theme }) => {
    switch (variant) {
      case 'secondary':
        return `
          background: ${theme.background.primary};
          color: ${theme.font.color.secondary};
          border: 1px solid ${theme.border.color.strong};
          &:hover:not(:disabled) {
            border-color: ${theme.accent.primary};
            color: ${theme.accent.primary};
          }
        `;
      case 'danger':
        return `
          background: ${theme.color.red};
          color: #fff;
          &:hover:not(:disabled) { filter: brightness(0.92); }
        `;
      case 'success':
        return `
          background: ${theme.color.green};
          color: #fff;
          &:hover:not(:disabled) { filter: brightness(0.92); }
        `;
      default: // primary
        return `
          background: ${theme.accent.primary};
          color: ${theme.font.color.inverted};
          &:hover:not(:disabled) { background: ${theme.accent.accent10}; }
        `;
    }
  }}
`;

export const IconButton = styled('button', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'variant'
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  color: ${({ theme }) => theme.font.color.secondary};
  cursor: pointer;
  transition: ${({ theme }) => theme.animation.transition.standard};

  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusShadow(theme.accent.tertiary)};
  }

  &:hover {
    border-color: ${({ theme }) => theme.accent.primary};
    color: ${({ theme }) => theme.accent.primary};
  }
`;

export const LinkButton = styled('button', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'variant'
})<{ variant?: 'danger' }>`
  background: none;
  border: none;
  padding: 0;
  font-size: ${({ theme }) => theme.font.size.md};
  font-family: ${({ theme }) => theme.font.family};
  color: ${({ theme, variant }) =>
    variant === 'danger' ? theme.color.red : theme.accent.primary};
  cursor: pointer;
  text-decoration: none;

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid ${({ theme, variant }) =>
      variant === 'danger' ? theme.color.red : theme.accent.primary};
    outline-offset: 2px;
  }

  &:hover { text-decoration: underline; }
`;
